import type { DOMConversionMap, EditorConfig, ElementFormatType, NodeKey, SerializedParagraphNode } from 'lexical';
import { $applyNodeReplacement, ParagraphNode, setNodeIndentFromDOM } from "lexical";
import type { DOMConversionOutput } from "lexical";

export type SerializedCustomParagraphNode = SerializedParagraphNode & {
	classes: string;
};

export class CustomParagraphNode extends ParagraphNode {
	__classes: string;

	static getType(): string {
		return "custom-paragraph";
	}

	static clone(node: CustomParagraphNode) {
		return new CustomParagraphNode(node.__classes, node.__key);
	}

	constructor(classes: string = '', key?: NodeKey) {
		super(key);
		this.__classes = classes;
	}

	getClasses(): string {
		return this.__classes;
	}

	setClasses(classes: string) {
		const writable = super.getWritable();
		writable.__classes = classes;
	}

	createDOM(config: EditorConfig): HTMLElement {
		const dom = super.createDOM(config);
		dom.className = this.getClasses();
		return dom;
	}

	updateDOM(prevNode: CustomParagraphNode, dom: HTMLElement, config: EditorConfig): boolean {
		const classes = this.__classes;

		if (classes !== prevNode.__classes) {
			dom.className = classes;
		}
		
		return false;
	}

	static importDOM(): DOMConversionMap {
		return {
			p: (node: Node) => ({
				conversion: $convertParagraphElement,
				priority: 1,
			}),
		};
	}

	static importJSON(serializedNode: SerializedCustomParagraphNode): CustomParagraphNode {
		const node = $createCustomParagraphNode();
		node.setFormat(serializedNode.format);
		node.setIndent(serializedNode.indent);
		node.setDirection(serializedNode.direction);
		node.setClasses(serializedNode.classes);
		return node;
	}

	exportJSON(): SerializedCustomParagraphNode {
		return {
			...super.exportJSON(),
			type: 'custom-paragraph',
			classes: this.getClasses(),
		};
	}
}

export function $createCustomParagraphNode(): CustomParagraphNode {
	return $applyNodeReplacement(new CustomParagraphNode());
}

export function $isCustomParagraphNode(node: any) {
	return node instanceof CustomParagraphNode;
}

function $convertParagraphElement(element: HTMLElement): DOMConversionOutput {
	const node = $createCustomParagraphNode();

	if (element.style) {
		node.setFormat(element.style.textAlign as ElementFormatType);
		setNodeIndentFromDOM(element, node);
	}

	if (element.className) {
		node.setClasses(element.className);
	}

	return {node};
}
